import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Crown, Plus, Trash2, Mail, Shield, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  const fetchSuperAdmins = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/super-admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuperAdmins(response.data.super_admins || []);
    } catch (error) {
      console.error('Error fetching super admins:', error);
      toast.error('Failed to load super admins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuperAdmin = async () => {
    if (!newAdminEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/super-admins`,
        { email: newAdminEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Super admin added successfully');
      setShowAddDialog(false);
      setNewAdminEmail('');
      fetchSuperAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add super admin');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSuperAdmin = async (userId) => {
    if (!confirm('Are you sure you want to remove super admin privileges from this user?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/super-admins/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Super admin privileges removed');
      fetchSuperAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove super admin');
    }
  };

  const filteredAdmins = superAdmins.filter(admin =>
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="super-admins-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="w-7 h-7 text-amber-500" />
            Super Administrators
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage users with full platform access
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-super-admin-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Super Admin
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Badge variant="outline">{filteredAdmins.length} admin(s)</Badge>
      </div>

      {/* Super Admins Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Super Admin Users</CardTitle>
          <CardDescription>
            Super admins have unrestricted access to all tenants and system settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No super admins found
                  </TableCell>
                </TableRow>
              ) : (
                filteredAdmins.map((admin) => (
                  <TableRow key={admin.id} data-testid={`admin-row-${admin.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Crown className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">{admin.full_name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{admin.role || 'Super Admin'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{admin.email}</TableCell>
                    <TableCell>
                      <Badge variant={admin.email_verified ? 'default' : 'secondary'}>
                        {admin.email_verified ? 'Verified' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveSuperAdmin(admin.id)}
                        data-testid={`remove-admin-${admin.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Super Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Super Administrator</DialogTitle>
            <DialogDescription>
              Enter the email of an existing user to grant super admin privileges.
              Super admins have full access to all tenants and system settings.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="user@company.com"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                type="email"
                data-testid="new-admin-email-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSuperAdmin} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminsPage;
